import { useState } from 'react';
import { Button, Card } from '@orion/ui';
import { useCreateNote, useNotesQuery } from './api.js';

interface NotesPanelProps {
  leadId?: string;
  contactId?: string;
  companyId?: string;
  opportunityId?: string;
  taskId?: string;
}

/** docs section 16 "Notes" — rich text is a follow-up (plain text today,
 * stored as a minimal ProseMirror-shaped doc so the API/schema doesn't need
 * to change when a real editor lands); author, timestamp and edit history
 * are already real (apps/api/src/modules/engagement/notes). */
export function NotesPanel(props: NotesPanelProps) {
  const { data, isLoading } = useNotesQuery(props);
  const createNote = useCreateNote(props);
  const [draft, setDraft] = useState('');

  const notes = data ?? [];

  const submit = async () => {
    if (!draft.trim()) return;
    await createNote.mutateAsync({
      bodyHtml: draft,
      bodyJson: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: draft }] }] },
      mentionedMembershipIds: [],
      ...props,
    });
    setDraft('');
  };

  return (
    <Card className="p-5">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-muted">Notes</h3>
      <div className="mb-4 flex flex-col gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Log a note…"
          rows={3}
          className="rounded-control border border-border-strong bg-surface px-3 py-2 text-sm"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={submit} loading={createNote.isPending} disabled={!draft.trim()}>
            Save note
          </Button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
      {!isLoading && notes.length === 0 && <p className="text-sm text-text-muted">No notes yet.</p>}
      <ul className="flex flex-col gap-3">
        {notes.map((n) => (
          <li key={n.id} className="border-b border-divider pb-3 last:border-b-0">
            <p className="text-sm text-ink">{n.bodyHtml}</p>
            <p className="mt-1 text-xs text-text-muted">
              {n.author?.displayName ?? 'Unknown'} · {new Date(n.createdAt).toLocaleString()}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
